// "Share" dialog for the admin app: shows a QR code and copyable link to the
// public, read-only spectator view (`/view/:id`). Anyone who scans the code or
// opens the link can follow live progress without an account — the unguessable
// tournament id in the URL is the capability.

import { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { QRCodeSVG } from 'qrcode.react';
import { useTranslation } from '../state/I18nContext';
import { publicViewUrl } from './public/sharePath';

interface ShareTournamentDialogProps {
  tournamentId: string;
  tournamentName: string;
  open: boolean;
  onClose: () => void;
}

export function ShareTournamentDialog({
  tournamentId,
  tournamentName,
  open,
  onClose,
}: ShareTournamentDialogProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  // Resolve against the current origin so the link works in dev and prod alike.
  const url = publicViewUrl(tournamentId, window.location.origin);

  // The body keeps the tournament name bold: without params, `{name}` stays
  // literal in the translated template, so split on it and style the name.
  const [bodyBefore, bodyAfter = ''] = t('admin.share.body').split('{name}');

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard API unavailable (e.g. insecure context); the link is still
      // selectable in the field, so fail silently.
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('admin.share.title')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {bodyBefore}
          <strong>{tournamentName}</strong>
          {bodyAfter}
        </Typography>

        <Stack alignItems="center" spacing={2}>
          {/* White, padded frame guarantees the quiet zone + contrast a scanner
              needs, regardless of the surrounding dark theme. */}
          <Box sx={{ bgcolor: '#fff', p: 2, borderRadius: 2, lineHeight: 0 }}>
            <QRCodeSVG value={url} size={200} level="M" marginSize={1} />
          </Box>

          <TextField
            value={url}
            fullWidth
            size="small"
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title={copied ? t('admin.share.copied') : t('admin.share.copyLink')} arrow>
                    <IconButton onClick={() => void copy()} edge="end" aria-label={t('admin.share.copyLink')}>
                      {copied ? <CheckIcon color="success" fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>{t('admin.common.close')}</Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant="contained"
          startIcon={<OpenInNewIcon />}
          component="a"
          href={url}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('admin.share.open')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
